import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { INTERNAL_ERROR_CODE, HTTP_STATUS_CODE } from "@/shared/constants/http";
import type { ApiResponse, UnifiedError } from "@/shared/types/api";

import { axiosCanceler } from "./canceler";
import { createApiClient, getApiRuntime } from "./client";

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  silent?: boolean; /** 是否静默请求 */
  skipCancelToken?: boolean;  /** 是否跳过重复请求取消 */
}

function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * AxiosHttpRequest 类封装了基于 Axios 的 HTTP 请求方法
 * @class AxiosHttpRequest
 * @param {AxiosRequestConfig} _config - Axios 请求配置
 */

class AxiosHttpRequest {
  private instance: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    this.instance = createApiClient(config);

    this.setupRequestInterceptor(); // 设置请求拦截器
    this.setupResponseInterceptor(); // 设置响应拦截器
  }

  private setupRequestInterceptor() {
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const customConfig = config as CustomAxiosRequestConfig & InternalAxiosRequestConfig;
        const runtime = getApiRuntime();

        if (!isOnline()) {
          const networkError: UnifiedError = {
            code: INTERNAL_ERROR_CODE.NETWORK,
            message: "Network unavailable",
          };
          runtime.onError?.(networkError, { silent: customConfig.silent });
        }

        const token = runtime.getToken?.();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        const privacyToken = runtime.getPrivacyToken?.();
        if (privacyToken && config.headers) {
          config.headers["X-Privacy-Token"] = privacyToken;
        }

        const language = runtime.getLanguage?.() ?? navigator.language;
        if (config.headers) {
          config.headers["Accept-Language"] = language;
          // config.headers["Client-Version"] = ENV.appVersion;
          // config.headers["X-User-Agent"] = navigator.userAgent;
        }

        // 添加到待取消列表
        if (!customConfig.skipCancelToken) {
          axiosCanceler.addPending(config);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private setupResponseInterceptor() {
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        const customConfig = response.config as CustomAxiosRequestConfig;
        // 移除待取消列表中的请求
        axiosCanceler.removePending(customConfig);

        // 直接返回后端响应体，不再需要 res.data.xxx
        return response.data;
      },
      (error: AxiosError<ApiResponse>) => {
        const customConfig = error.config as CustomAxiosRequestConfig;
        const errorCode = error.code;
        const isCanceled = axios.isCancel(error) || errorCode === "ERR_CANCELED";
        const silent = customConfig.silent || isCanceled;
        const { response, code } = error;
        const runtime = getApiRuntime();

        axiosCanceler.removePending(customConfig);

        let errCode: number = INTERNAL_ERROR_CODE.UNKNOWN;
        let errMessage = "Request failed";
        let errReason: string | undefined;
        let responseStatus: number | undefined;

        if (isCanceled) {
          errCode = INTERNAL_ERROR_CODE.CANCELLED;
          errMessage = "Request cancelled";
        } else if (!response) {
          if (code === 'ECONNABORTED') {
            errCode = INTERNAL_ERROR_CODE.TIMEOUT;
            errMessage = "Request timeout";
          } else if (code === 'ERR_NETWORK') {
            errCode = INTERNAL_ERROR_CODE.NETWORK;
            errMessage = "Network error";
          }
        } else {
          const { status, data: resData } = response;
          errReason = resData.reason || undefined;
          const httpStatus = status;

          if (resData.code) {
            errCode = resData.code;
            errMessage = resData.message || errMessage;
          }

          if (httpStatus === HTTP_STATUS_CODE.UNAUTHORIZED) {
            axiosCanceler.clearPending();
            runtime.onUnauthorized?.();
          }

          if (httpStatus === HTTP_STATUS_CODE.BAD_REQUEST && resData.message) {
            errMessage = resData.message;
          }

          if (httpStatus >= HTTP_STATUS_CODE.SERVER_ERROR) {
            errMessage = "Server error";
          }

          responseStatus = httpStatus;
        }

        const unifiedError: UnifiedError = {
          code: errCode,
          message: errMessage,
          reason: errReason,
          status: responseStatus,
        };
        runtime.onError?.(unifiedError, { silent, status: responseStatus, reason: errReason });
        return Promise.reject(unifiedError);
      }
    );
  }


  public get<T>(url: string, params?: object, config?: CustomAxiosRequestConfig): Promise<T> {
    return this.instance.get(url, { ...config, params: params });
  }

  public post<T>(url: string, data?: object, config?: CustomAxiosRequestConfig): Promise<T> {
    return this.instance.post(url, data, config);
  }

  public put<T>(url: string, data?: object, config?: CustomAxiosRequestConfig): Promise<T> {
    return this.instance.put(url, data, config);
  }

  public delete<T>(url: string, params?: object, config?: CustomAxiosRequestConfig): Promise<T> {
    return this.instance.delete(url, { ...config, params: params });
  }

  public upload<T>(url: string, formData: FormData, config?: CustomAxiosRequestConfig): Promise<T> {
    return this.instance.post(url, formData, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data', ...config?.headers },
    });
  }

  public download(url: string, params?: object, config?: CustomAxiosRequestConfig): Promise<Blob> {
    return this.instance.get(url, { params, responseType: 'blob', ...config });
  }
}

export default new AxiosHttpRequest({});
